/**
 * Maps v2 upcoming bookings API responses to the legacy shape still used by the
 * upcoming-calls UI (call cards, reschedule modal, etc.).
 *
 * V1 has been decommissioned, so this is no longer bridging two live API
 * versions — it's a permanent internal shape-adapter that keeps the (still
 * widely used) legacy field names decoupled from the v2 API response shape.
 * Only worth removing alongside a dedicated migration of every upcoming-calls
 * consumer to v2 field names directly.
 */

import {BaseSessionAndBundleApiResponse} from "../types";
import {
  GetCoachUpcomingCallsApiResponse,
  GetCoachUpcomingCallsApiResponseTransaction,
  GetCustomerUpcomingCallsApiResponse,
  GetCustomerUpcomingCallsApiResponseSession,
} from "./types";

type V2UpcomingBookingItem = {
  id: number;
  reference: string;
  /**
   * Returned by `bookings/upcoming`. The reschedule drawer needs it to query
   * availability without a separate booking fetch, so it is threaded through
   * rather than stubbed to 0. Optional for tolerance, not because it is missing.
   */
  serviceId?: number;
  serviceName: string;
  serviceDescription?: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string | null;
  currency: string;
  amount: number;
  bookingYear: string;
  bookingMonth: string;
  bookingDate: number;
  coachFromTime: string;
  coachToTime: string;
  timeslot: string;
  duration: number;
  bookingType: "video" | "audio";
  bundleSession: string | number | null;
  bundleSessionCount?: number;
  videoPlatform: GetCoachUpcomingCallsApiResponseTransaction["videoPlatform"];
  roomId: string | null;
  coachFirstName?: string;
  coachLastName?: string;
  coachUsername?: string;
  coachDisplayName?: string | null;
  bookingQuestions?: string | null;
};

type V2UpcomingBookingsResponse = {
  data?: V2UpcomingBookingItem[];
  total?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
};

const EMPTY_MENTOR: GetCoachUpcomingCallsApiResponse["mentor"] = {
  avatar: null,
  country: "",
  cover_image: null,
  email: "",
  firstName: "",
  id: 0,
  isSocialAuth: false,
  lastName: "",
  onboarding_reminder: 0,
  password: null,
  statusId: 0,
  updatedAt: "",
};

const EMPTY_MENTOR_PROFILE: GetCoachUpcomingCallsApiResponse["mentorProfile"] = {
  behance: null,
  buffer_time: 0,
  createdAt: "",
  description: null,
  discord: null,
  dribble: null,
  facebook: null,
  github: null,
  how_did_you_find_us: null,
  id: 0,
  instagram: null,
  linkedin: null,
  shortBio: null,
  tiktok: null,
  twitch: null,
  twitter: null,
  updatedAt: "",
  userId: 0,
  username: "",
  website: null,
  youtube: null,
};

function createServiceStub(item: V2UpcomingBookingItem): BaseSessionAndBundleApiResponse {
  const isBundle = item.bundleSession != null;

  return {
    id: item.serviceId ?? 0,
    name: item.serviceName,
    description: item.serviceDescription ?? "",
    callDuration: item.duration,
    callPrice: item.amount,
    callPriceCurrency: item.currency,
    audioLocation: item.bookingType === "audio",
    videoLocation: item.bookingType === "video",
    bookingQuestions: null,
    cover_picture: null,
    cover_images: [],
    createdAt: "",
    hideServiceFromMenu: false,
    request_phone_number: false,
    slug: "",
    tiny_url: "",
    updatedAt: "",
    url_redirect: null,
    userId: 0,
    videoPlatform: item.videoPlatform,
    ...(isBundle
      ? {
          is_bundle: true as const,
          no_of_sessions: item.bundleSessionCount ?? 0,
          booking_interval: "1week" as const,
        }
      : {
          is_bundle: false as const,
          no_of_sessions: null,
          booking_interval: null,
        }),
  };
}

function normalizeV2UpcomingBookingItem(
  item: V2UpcomingBookingItem
): GetCoachUpcomingCallsApiResponseTransaction {
  const isBundle = item.bundleSession != null;
  const isVideo = item.bookingType === "video";

  return {
    id: item.id,
    reference: item.reference,
    amount: item.amount,
    currency: item.currency,
    booking_date: item.bookingDate,
    booking_month: String(item.bookingMonth),
    booking_year: String(item.bookingYear),
    coach_from_time: item.coachFromTime,
    coach_to_time: item.coachToTime,
    timeslot: item.timeslot,
    first_name: item.firstName,
    last_name: item.lastName,
    email_address: item.emailAddress,
    bookingQuestions: item.bookingQuestions || "[]",
    cancellationReason: null,
    charge_bearer: "coach",
    charges: 0,
    createdAt: "",
    endTime: "",
    is_paid: item.amount > 0,
    mentor_timezone: "",
    notes: null,
    payment_provider: "paystack",
    roomId: item.roomId,
    videoPlatform: item.videoPlatform,
    video_platform: item.videoPlatform,
    service: createServiceStub(item),
    serviceId: item.serviceId ?? 0,
    statusId: 1,
    statusName: "Active",
    updatedAt: "",
    userId: 0,
    user_timezone: "",
    discount_code: null,
    is_discounted: false,
    booking_type: item.bookingType,
    ...(isBundle
      ? {
          is_bundle: true as const,
          bundle_reference: "",
          // v2 returns a number; the legacy transaction contract expects a string.
          bundle_session: String(item.bundleSession),
        }
      : {
          is_bundle: false as const,
          bundle_reference: null,
          bundle_session: null,
        }),
    ...(isVideo ? {phone_number: null} : {phone_number: item.phoneNumber ?? ""}),
  } as GetCoachUpcomingCallsApiResponseTransaction;
}

export function normalizeV2CoachUpcomingCallsResponse(
  v2Data: V2UpcomingBookingsResponse
): GetCoachUpcomingCallsApiResponse {
  const items = v2Data.data ?? [];

  return {
    transactions: items.map(normalizeV2UpcomingBookingItem),
    mentor: EMPTY_MENTOR,
    mentorProfile: EMPTY_MENTOR_PROFILE,
  };
}

export function normalizeV2CustomerUpcomingCallsResponse(
  v2Data: V2UpcomingBookingsResponse
): GetCustomerUpcomingCallsApiResponse {
  const items = v2Data.data ?? [];

  return {
    session: items.map(
      (item): GetCustomerUpcomingCallsApiResponseSession => ({
        ...normalizeV2UpcomingBookingItem(item),
        coach_first_name: item.coachFirstName ?? "",
        coach_last_name: item.coachLastName ?? "",
        coach_username: item.coachUsername ?? "",
        coach_display_name: item.coachDisplayName ?? null,
      })
    ),
  };
}

export function normalizeV2CustomerUpcomingCallsMeta(
  v2Data: V2UpcomingBookingsResponse,
  page: number,
  perPage: number
) {
  const total = v2Data.total ?? v2Data.data?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return {
    current_page: page,
    per_page: perPage,
    total_pages: totalPages,
    next_page: v2Data.hasNextPage ? page + 1 : 0,
    previous_page: v2Data.hasPrevPage ? page - 1 : 0,
  };
}
